import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import relationships from 'Consts/Relationships';

import useCollection from 'Hooks/useCollection';
import useResourceDeprecated from 'Hooks/useResourceDeprecated';
import { displayErrorMessage } from 'Helpers/FacetNotificationManager';

import useUserContext from 'Context/UserContext';
import { useNoteContext } from 'Context/NoteContext';
import { pickImplementation } from 'Helpers/Affordances';
import DataContainer from 'Components/UI/DataContainer';
import fr from 'facet-rest';
import Notes from './index';

function Container({
  householdResource,
  meetingResource,
  ...rest
}) {
  const [loading, setLoading] = useState(true);
  const [loadingError, setLoadingError] = useState(null);
  const { collection: templateCollection, setCollection: setTemplateCollection } = useCollection();
  const [selectedTemplate, setSelectedTemplate] = useState();
  const { resource, setResource } = useResourceDeprecated();
  const { userResource } = useUserContext();
  const { setNoteResourceObject } = useNoteContext();

  useEffect(() => {
    const noteRel = meetingResource.withRelationship(fr.relationships.notes.name, (rel) => {
      return rel;
    }, () => null);
    if (!noteRel?.link('related')) {
      return null;
    }

    setLoading(true);
    setLoadingError(null);
    meetingResource
      .relationship(relationships.notes)
      .link('related')
      .Get()
      .then((coll) => {
        let tmpNoteResource;

        switch (coll.resources().length()) {
          case 0:
            coll.create({ addToCollection: true });

            tmpNoteResource = coll.resources().head()
              // TODO: Fix issue where resources created by collections do not have sensible values populated for attributes.
              .attr('content', '')
              .attr('noteType', 'meeting');
            break;
          case 1:
            tmpNoteResource = coll.resources().head();
            break;
          default:
            throw new Error(
              `More than 1 note returned for Meeting (${meetingResource.id()})`,
            );
        }

        setResource(tmpNoteResource);
        setNoteResourceObject({ noteResource: tmpNoteResource, noteResourceSetBy: 'CurrentNote' });
      })
      .catch((err) => {
        displayErrorMessage(err, err.errors);
        setLoadingError(err);
      })
      .then(() => {
        setLoading(false);
      });

    return () => {
      setResource(null);
      setNoteResourceObject({
        noteResource: null,
        noteResourceSetBy: null,
      });
    };
  }, [meetingResource?.id()]);

  const getNoteTemplateCollection = async () => {
    const collection = await meetingResource
      .relationship(relationships.noteTemplates)
      .link('related')
      .Get({ params: { limit: 100, enabled: 'true' } });
    setTemplateCollection(collection);
  };

  const updateSelectedTemplate = (selectedTemplateId) => {
    const template = templateCollection.resources().list().find(templateResource => templateResource.id() === selectedTemplateId);
    setSelectedTemplate(template || templateCollection.create());
  };

  const saveSelectedTemplate = async (content) => {
    let templateToSave;
    if (selectedTemplate.id() && !selectedTemplate.attr('agentId')) {
      templateToSave = templateCollection.create().attr('name', selectedTemplate.attr('name'));
    } else {
      templateToSave = selectedTemplate;
    }
    const newTemplate = (await templateToSave
      .attr('content', content)
      .attr('agentId', userResource.attr('agentId'))
      .submit())
      .resources()
      .head();

    await getNoteTemplateCollection();
    setSelectedTemplate(newTemplate);
  };

  const isValidTemplateName = name => !templateCollection
    .resources()
    .list()
    .find(template => template.attr('agentId') && template.attr('name') === name);

  useEffect(() => {
    const effect = pickImplementation(meetingResource, {
      [relationships.default]: () => getNoteTemplateCollection(),
    });

    effect();
  }, []);

  return (
    <DataContainer
      key={resource?.link('self')?.url()}
      component={Notes}
      componentProps={{
        householdResource,
        noteResource: resource,
        meetingResource,
        templateCollection,
        selectedTemplate,
        updateSelectedTemplate,
        saveSelectedTemplate,
        isValidTemplateName,
        getNoteCollection: getNoteTemplateCollection,
        ...rest,
      }}
      loading={(loadingError) ? null : (loading || !resource)}
      loadingError={loadingError}
    />
  );
}

Container.propTypes = {
  householdResource: PropTypes.object.isRequired,
  meetingResource: PropTypes.object.isRequired,
};

export default Container;
